import { computed, inject, Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { catchError, of, tap } from 'rxjs';
import { GraphService } from './graph.service';
import { environment } from '../../../environments/environment';
import { EntraRoles, TokenClaims } from '@monorepo/interfaces';
import { Store } from '@ngxs/store';
import { LoadUser } from '../../user-management/state/user-management.actions';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private msalService = inject(MsalService);
    private msalBroadcastService = inject(MsalBroadcastService);
    private graphService = inject(GraphService);
    private store = inject(Store);

    private _isAuthenticated: WritableSignal<boolean> = signal<boolean>(false);
    private _isAuthLoading: WritableSignal<boolean> = signal<boolean>(true);
    private _userAccount: WritableSignal<AccountInfo | null> = signal<AccountInfo | null>(null);
    private _userPhoto: WritableSignal<Blob | null> = signal<Blob | null>(null);
    private _isPhotoLoading: WritableSignal<boolean> = signal<boolean>(false);
    private _photoError: WritableSignal<string | null> = signal<string | null>(null);

    public isAuthenticated: Signal<boolean> = computed(() => this._isAuthenticated());
    public isAuthLoading: Signal<boolean> = computed(() => this._isAuthLoading());
    public userIdTokenClaims: Signal<TokenClaims | null> = computed(
        () => this._userAccount()?.idTokenClaims as TokenClaims
    );
    public userName: Signal<string | null> = computed(
        () => `${this.userIdTokenClaims()?.preferred_family_name}, ${this.userIdTokenClaims()?.preferred_given_name}`
    );
    public userEntraRoles: Signal<EntraRoles[]> = computed(
        () => (this.userIdTokenClaims()?.roles as EntraRoles[]) ?? []
    );

    constructor() {
        this.initializeAuth();
    }

    public logout(): void {
        this._userPhoto.set(null);
        this.msalService.logoutRedirect();
    }

    public getUserPhotoUrl(): string | null {
        const photo = this._userPhoto();
        return photo ? URL.createObjectURL(photo) : null;
    }

    private initializeAuth(): void {
        this.subscribeToLogin();
        this.subscribeToLogout();
    }

    private subscribeToLogin(): void {
        this.msalBroadcastService.inProgress$
            .pipe(
                tap((status: InteractionStatus) => {
                    this._isAuthLoading.set(status !== InteractionStatus.None);

                    if (status === InteractionStatus.None) {
                        this.processLoginDoneAndUpdateUserData();
                    }
                })
            )
            .subscribe();
    }

    private subscribeToLogout(): void {
        this.msalBroadcastService.msalSubject$
            .pipe(
                tap((msg: EventMessage) => {
                    if (msg.eventType === EventType.LOGOUT_SUCCESS) {
                        this._isAuthenticated.set(false);
                        this._userAccount.set(null);
                        this._userPhoto.set(null);
                    }
                })
            )
            .subscribe();
    }

    private processLoginDoneAndUpdateUserData(): void {
        const accounts = this.msalService.instance.getAllAccounts();

        if (accounts.length > 0) {
            const activeAccount = this.msalService.instance.getActiveAccount() || accounts[0];

            if (activeAccount) {
                this.msalService.instance.setActiveAccount(activeAccount);
                this._userAccount.set(activeAccount);
                this._isAuthenticated.set(true);

                this.store.dispatch(new LoadUser());
                this.loadUserPhoto();
            } else {
                this.setUnauthorizedUserSignals();
            }
        } else {
            this.setUnauthorizedUserSignals();
        }

        this._isAuthLoading.set(false);
    }

    private loadUserPhoto(): void {
        this._isPhotoLoading.set(true);
        this._photoError.set(null);

        this.graphService
            .getPhotoForUser(this.userIdTokenClaims()?.oid ?? 'me', environment.apiConfig[0].scopes)
            .pipe(
                tap(photo => {
                    this._userPhoto.set(photo);
                    this._isPhotoLoading.set(false);
                }),
                catchError(error => {
                    this._photoError.set('Failed to load user photo');
                    this._isPhotoLoading.set(false);
                    console.error('Error loading user photo:', error);
                    return of(null);
                })
            )
            .subscribe();
    }

    private setUnauthorizedUserSignals() {
        this._isAuthenticated.set(false);
        this._userAccount.set(null);
        this._userPhoto.set(null);
    }

    public login(): void {
        if (!this.isAuthenticated()) {
            this.msalService.loginRedirect();
        }
    }
}
